import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Lock,
  Unlock,
  TrendingUp,
  TrendingDown,
  Calendar,
  DollarSign,
  BarChart3,
  FileText,
  AlertCircle,
  Download,
} from 'lucide-react';

export default function ClosingsHistoryPage() {
  const { API, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [closings, setClosings] = useState([]);
  const [selectedClosing, setSelectedClosing] = useState(null);
  const [showReopenDialog, setShowReopenDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadClosings();
  }, []);

  const loadClosings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/closings`, { withCredentials: true });
      setClosings(response.data);
    } catch (error) {
      toast.error('Erro ao carregar histórico de fechamentos');
      console.error('Error loading closings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReopenClick = (closing) => {
    setSelectedClosing(closing);
    setShowReopenDialog(true);
  };

  const handleReopen = async () => {
    if (!password) {
      toast.error('Digite sua senha para confirmar');
      return;
    }

    setSubmitting(true);
    try {
      await axios.delete(`${API}/reports/closing/${selectedClosing.closing_id}`, {
        data: { password },
        withCredentials: true
      });
      toast.success(`Fechamento de ${meses[selectedClosing.mes - 1]}/${selectedClosing.ano} reaberto com sucesso!`);
      setShowReopenDialog(false);
      setPassword('');
      loadClosings();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao reabrir fechamento';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPDF = async (closing) => {
    try {
      toast.info('Gerando PDF do relatório...');
      
      // Fazer requisição para gerar o PDF
      const response = await axios.get(`${API}/reports/pdf`, {
        params: {
          mes: closing.mes,
          ano: closing.ano,
          percentual: closing.percentual_aplicado,
          titulo_percentual: 'Distribuição',
          observacoes: closing.observacoes || ''
        },
        responseType: 'blob',
        withCredentials: true
      });

      // Criar URL do blob e fazer download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_financeiro_${meses[closing.mes - 1].toLowerCase()}_${closing.ano}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('PDF baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
      toast.error('Erro ao gerar PDF do relatório');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const meses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  // Calcular estatísticas
  const stats = {
    total: closings.length,
    totalEntradas: closings.reduce((acc, c) => acc + c.total_entradas, 0),
    totalSaidas: closings.reduce((acc, c) => acc + c.total_saidas, 0),
    saldoAcumulado: closings.reduce((acc, c) => acc + c.saldo_liquido, 0),
  };

  // Dados para o gráfico de evolução (últimos 12 meses)
  const last12Closings = closings.slice(0, 12).reverse();

  // Calcular tendência (comparar últimos 3 vs 3 anteriores)
  const calcTrend = () => {
    if (closings.length < 6) return { trend: 0, isPositive: true };
    
    const recent3 = closings.slice(0, 3).reduce((acc, c) => acc + c.saldo_liquido, 0) / 3;
    const previous3 = closings.slice(3, 6).reduce((acc, c) => acc + c.saldo_liquido, 0) / 3;
    
    const trend = ((recent3 - previous3) / previous3) * 100;
    return { trend: Math.abs(trend), isPositive: trend >= 0 };
  };

  const trendData = calcTrend();

  return (
    <div className="space-y-6" data-testid="closings-history-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope]">Histórico de Fechamentos</h1>
          <p className="text-gray-500 text-sm">Visualize e gerencie fechamentos mensais realizados</p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total de Fechamentos</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Entradas Acumuladas</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalEntradas)}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Saídas Acumuladas</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(stats.totalSaidas)}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Saldo Acumulado</p>
                <p className="text-2xl font-bold text-blue-600">{formatCurrency(stats.saldoAcumulado)}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trend and Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trend Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Tendência Recente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                {trendData.isPositive ? (
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <TrendingDown className="w-6 h-6 text-red-600" />
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-500">Últimos 3 meses vs anteriores</p>
                  <p className={`text-2xl font-bold ${trendData.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                    {trendData.isPositive ? '+' : '-'}{trendData.trend.toFixed(1)}%
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                {trendData.isPositive 
                  ? 'Seu saldo líquido está crescendo! Continue assim.' 
                  : 'Atenção: saldo líquido em queda nos últimos meses.'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Simple Bar Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Evolução do Saldo Líquido (Últimos 12 Meses)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48 flex items-end justify-between gap-2">
              {last12Closings.length === 0 ? (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <p>Nenhum fechamento realizado ainda</p>
                </div>
              ) : (
                last12Closings.map((closing, idx) => {
                  const maxValue = Math.max(...last12Closings.map(c => c.saldo_liquido));
                  const height = (closing.saldo_liquido / maxValue) * 100;
                  const isPositive = closing.saldo_liquido >= 0;
                  
                  return (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                      <div 
                        className={`w-full rounded-t ${isPositive ? 'bg-green-500' : 'bg-red-500'} transition-all hover:opacity-80 cursor-pointer`}
                        style={{ height: `${Math.max(height, 5)}%` }}
                        title={`${meses[closing.mes - 1]}/${closing.ano}: ${formatCurrency(closing.saldo_liquido)}`}
                      />
                      <span className="text-xs text-gray-500 transform -rotate-45 origin-top-left mt-2">
                        {closing.mes}/{closing.ano.toString().slice(2)}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Closings Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Histórico Detalhado
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : closings.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Lock className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum fechamento realizado ainda</p>
              <p className="text-sm mt-1">Realize seu primeiro fechamento no Relatório Financeiro</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Período</TableHead>
                  <TableHead className="text-right">Entradas</TableHead>
                  <TableHead className="text-right">Saídas</TableHead>
                  <TableHead className="text-right">Saldo Líquido</TableHead>
                  <TableHead className="text-right">Percentual</TableHead>
                  <TableHead className="text-right">Valor Calculado</TableHead>
                  <TableHead>Data do Fechamento</TableHead>
                  <TableHead className="text-center">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {closings.map((closing) => (
                  <TableRow key={closing.closing_id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-gray-400" />
                        {meses[closing.mes - 1]}/{closing.ano}
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-green-600 font-medium">
                      {formatCurrency(closing.total_entradas)}
                    </TableCell>
                    <TableCell className="text-right text-red-600 font-medium">
                      {formatCurrency(closing.total_saidas)}
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      <span className={closing.saldo_liquido >= 0 ? 'text-blue-600' : 'text-red-600'}>
                        {formatCurrency(closing.saldo_liquido)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right text-gray-600">
                      {closing.percentual_aplicado}%
                    </TableCell>
                    <TableCell className="text-right text-purple-600 font-medium">
                      {formatCurrency(closing.valor_calculado)}
                    </TableCell>
                    <TableCell className="text-gray-500 text-sm">
                      {formatDate(closing.created_at)}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadPDF(closing)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Baixar PDF do relatório"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                        {user?.role === 'admin' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReopenClick(closing)}
                            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                            title="Reabrir fechamento (admin)"
                          >
                            <Unlock className="w-4 h-4 mr-1" />
                            Reabrir
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Reopen Dialog */}
      <AlertDialog open={showReopenDialog} onOpenChange={setShowReopenDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              Reabrir Fechamento Mensal
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                Você está prestes a <strong>reabrir o fechamento de {selectedClosing && `${meses[selectedClosing.mes - 1]}/${selectedClosing.ano}`}</strong>.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800 font-medium">
                  ⚠️ Atenção: Esta ação irá deletar permanentemente o registro de fechamento.
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  Você poderá realizar um novo fechamento para este mês após reabri-lo.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password" className="text-sm font-medium">
                  Digite sua senha de administrador para confirmar:
                </Label>
                <Input
                  id="admin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Senha do administrador"
                  className="w-full"
                  autoFocus
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setPassword(''); setShowReopenDialog(false); }}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReopen}
              disabled={submitting || !password}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {submitting ? 'Processando...' : 'Confirmar Reabertura'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
